import { useEffect, useState, useCallback } from 'react'
import { useSessionContext } from '../contexts/SessionContext'
import { supabase } from '../utils/helpers/supabase.helpers'

interface UseExperienceSharingInfoParams {
  experienceId: string
}

interface ExperienceSharingInfo {
  sharedWith?: Array<{
    sharingId: string
    role: number
    sharedAt: string
    userId: string
    userEmail: string
    /** Three-state: an access-code expiry disables a bond without deleting it. */
    isEnabled: boolean | null
  }>
  anonymouShareCount?: number
}

interface UseExperienceSharingInfoReturn {
  experienceSharingInfo: ExperienceSharingInfo | null
  isLoading: boolean
  refetch: () => Promise<void>
}

export const useExperienceSharingInfo = ({
  experienceId,
}: UseExperienceSharingInfoParams): UseExperienceSharingInfoReturn => {
  const { user } = useSessionContext()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [experienceSharingInfo, setExperienceSharingInfo] = useState<ExperienceSharingInfo | null>(null)

  const fetchExperienceSharingInfo = useCallback(async (): Promise<void> => {
    if (!experienceId || !user) return

    setIsLoading(true)

    const { data, error } = await supabase.functions.invoke('get-experience-sharing-info', {
      body: JSON.stringify({ experienceId }),
    })

    if (error) {
      setIsLoading(false)
      return
    }

    setExperienceSharingInfo(data)
    setIsLoading(false)
  }, [user, experienceId])

  useEffect(() => {
    if (!user || !experienceId) {
      return
    }
    fetchExperienceSharingInfo()
  }, [user, experienceId, fetchExperienceSharingInfo])

  return { experienceSharingInfo, isLoading, refetch: fetchExperienceSharingInfo }
}
